import * as React from "react";
import { ConnectProto, type ApiError } from "@racoon/api";
import { Namespace, useApiErrorMessage, useTypedT } from "@racoon/i18n";
import { cn, CountBadge, useToast } from "@racoon/ui";
import { fromTimestamp, getEnumValue } from "@racoon/lib";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { ShiftPlayback } from "~/pages/dashboard/gps-tracking/components/shift-playback";
import { CollapsibleMapPanel } from "~/features/map-view/components/CollapsibleMapPanel";
import { useMapPanelCollapsed } from "~/features/map-view/hooks/use-map-panel-collapsed";
import { useListDestinationLocationsQuery } from "~/shared/api/locations/locations.grpc";
import { useListUsersQuery } from "~/shared/api/user/user.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { fullName } from "~/shared/helpers";
import { downloadBlob, printBlob } from "~/shared/lib/export-utils";
import { useMswRouteListState } from "../hooks/msw-route-list/useMswRouteListState";
import { MswRouteListSidebar } from "./msw-route-list/MswRouteListSidebar";
import { MswRouteCascade } from "./msw-route-list/MswRouteCascade";
import { unloadLocationsArgs } from "./msw-route-list/unload-locations";
import { TtnHeaderActions, TtnRouteInfoContent } from "./msw-route-list/ttn-route-info.content";
import { ApprovalPill, FolderTabs, ShiftHeader, ShiftSummary } from "./msw-route-list/shift-header";
import { SourceLegend } from "./msw-route-list/ttn-source";
import { TtnEditConfirmModal } from "./msw-route-list/ttn-edit-confirm.modal";
import { TtnSnapshotHost, type TtnSnapshotNodes } from "./msw-route-list/ttn-snapshot.host";
import { useTtnDraft } from "../hooks/ttn/useTtnDraft";
import { isShownOnTtn } from "../hooks/ttn/ttnDraftUtils";
import { isTtnApproved } from "~/shared/lib/shift-status";
import { storedTtnView } from "../hooks/ttn/ttnStoredView";

const EMPTY_ROUTES: ConnectProto.Route[] = [];
const EMPTY_USERS: ConnectProto.User[] = [];
const EMPTY_LOCATIONS: ConnectProto.Location[] = [];

type TtnTab = "info" | "jobs" | "fuel";

// The map column's expanded width, in units of the content area (sidebar
// excluded) — the same share the old 1073fr/600fr grid split gave it. Declared
// on the container so the panel and its fixed-size inner map resolve it against
// the same box, not against their own (collapsing) width.
const TTN_MAP_WIDTH_VARS = "[container-type:inline-size] [--ttn-map-w:36cqw]";
const TTN_MAP_PANEL_WIDTH = "w-[var(--ttn-map-w)]";

const formatDriverName = (shift: ConnectProto.DriverOnShift | undefined, fallbackName: string) =>
  fullName(shift?.driver) || fallbackName;

/** Centered muted message in place of a tab's content. */
const EmptyState: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => (
  <div className={`flex items-center justify-center ${className ?? "h-24"}`}>
    <span className="text-sm text-muted-foreground">{children}</span>
  </div>
);

// Flags a vehicle in the sidebar until every shift's TTN is approved; only then it gets the
// check. A cancelled shift has no TTN, so it never holds the flag up.
const isTtnNotApproved = (shift: ConnectProto.DriverOnShift) =>
  shift.status !== ConnectProto.DriverOnShiftStatus.CANCELLED && !isTtnApproved(shift);

export const TtnRouteListPage: React.FC = () => {
  const t = useTypedT(Namespace.Routes);
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const [activeTab, setActiveTab] = React.useState<TtnTab>("info");
  const { collapsed: mapCollapsed, toggle: toggleMapCollapsed } =
    useMapPanelCollapsed("ttn-route-list");

  const [searchParams] = useSearchParams();
  const truckTypeFromUrl = searchParams.get("truck_type");
  const truckType = truckTypeFromUrl
    ? getEnumValue(ConnectProto.TruckTypeSchema, truckTypeFromUrl)
    : undefined;

  const {
    selectedDate,
    setSelectedDate,
    vehicleSearch,
    setVehicleSearch,
    sidebarMode,
    setSidebarMode,
    sidebarSections,
    selectedId,
    selectedVehicle,
    selectedVehicleShifts,
    selectedShift,
    expandedRouteByShiftId,
    focusedShiftLoading,
    handleSidebarSelect,
    handleToggleShift,
    handleExpandRoute,
    handleRoutesUpdate,
    handleLoadingUpdate,
    handleSelectionMapUpdate,
    allRoutesByShiftId,
    refetchAllRoutes,
    refetchShifts,
  } = useMswRouteListState({ truckType });

  const activeShiftId = selectedShift?.id ?? null;

  const shiftRoutes = React.useMemo(() => {
    if (activeShiftId === null) return EMPTY_ROUTES;
    const routes = allRoutesByShiftId.get(activeShiftId) ?? EMPTY_ROUTES;
    return [...routes].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }, [activeShiftId, allRoutesByShiftId]);
  // A cancelled trip was never driven, so the summary does not count it.
  const shownShiftRoutes = React.useMemo(() => shiftRoutes.filter(isShownOnTtn), [shiftRoutes]);

  // The routes cascade normally feeds routes/loading back into the hook, but on
  // the TTN page it only mounts on the "completed jobs" tab. Push them here so
  // the summary, map and approval state stay populated on every tab.
  React.useEffect(() => {
    if (activeShiftId === null) return;
    handleRoutesUpdate(activeShiftId, shownShiftRoutes);
  }, [activeShiftId, shownShiftRoutes, handleRoutesUpdate]);

  React.useEffect(() => {
    if (activeShiftId === null) return;
    handleLoadingUpdate(activeShiftId, focusedShiftLoading);
  }, [activeShiftId, focusedShiftLoading, handleLoadingUpdate]);

  // Stable, because the cascade re-sends its selection whenever this identity
  // changes and the update always stores a new Map: an inline arrow loops.
  const handleShiftSelectionMapChange = React.useCallback(
    (selectionMap: Map<bigint, Set<bigint>>) => {
      if (activeShiftId === null) return;
      handleSelectionMapUpdate(activeShiftId, selectionMap);
    },
    [activeShiftId, handleSelectionMapUpdate],
  );

  const { selectedCompanyId, selectedCompany } = useSelectedCompany();
  const orgId = selectedCompanyId ? BigInt(selectedCompanyId) : null;

  // Destination locations, not just the org's own: a trip unloads at disposal
  // sites owned by other organizations (linked through organization settings),
  // and a plain `listLocations` would leave those unload places unresolved.
  // currentData, not data: after a company switch the previous company's
  // locations must not stay pickable while the new list loads.
  const { currentData: depotsResponse } = useListDestinationLocationsQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      types: [
        ConnectProto.LocationType.DEPOT,
        ConnectProto.LocationType.DISPOSAL_SITE,
        ConnectProto.LocationType.GAS_STATION,
        ConnectProto.LocationType.PROCESSING_PLANT,
        ConnectProto.LocationType.SORTING_COMPLEX,
        ConnectProto.LocationType.TRANSFER_STATION,
      ],
    },
    { skip: !selectedCompanyId },
  );

  // Unfiltered on purpose: the pickers offer only active locations, but a trip's
  // saved place is resolved against the whole list — one deactivated later is
  // still where that trip unloaded and must keep its name.
  const depots = depotsResponse?.locations ?? EMPTY_LOCATIONS;

  // The jobs tab's unload pickers name their place from this list. Loaded here
  // first (the request is shared), so the PDF never captures them still loading —
  // nor the main tab's places, which come from `depots`.
  const { currentData: unloadLocationsResponse } = useListDestinationLocationsQuery(
    unloadLocationsArgs(orgId ?? 0n),
    { skip: orgId === null },
  );
  const areLocationsReady =
    orgId === null || (unloadLocationsResponse !== undefined && depotsResponse !== undefined);

  const expandedRouteId = activeShiftId !== null ? expandedRouteByShiftId.get(activeShiftId) : null;

  const { currentData: crewCandidatesResponse } = useListUsersQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      roles: [ConnectProto.UserRole.PROVIDER_LOADER],
    },
    { skip: !selectedCompanyId },
  );
  const crewCandidates = crewCandidatesResponse?.users ?? EMPTY_USERS;
  const ttn = useTtnDraft(selectedShift, shiftRoutes, crewCandidates);
  const [isApprovingTtn, setIsApprovingTtn] = React.useState(false);

  // Done-ness is the backend's call: it stamps `consignmentNoteDoneAt` once the
  // shift, every trip and every service act are filled — there is no "mark done".
  const isTtnDone = isTtnApproved(selectedShift);
  // The TTN records a finished shift: until it has ended nothing can be filled in.
  const isShiftEnded = selectedShift?.status === ConnectProto.DriverOnShiftStatus.ENDED;
  // An approved TTN is read-only. "Edit", once confirmed, unlocks it for that
  // shift; the edits stay in the draft until "Save", and "Cancel" drops them.
  const [editingShiftId, setEditingShiftId] = React.useState<bigint | null>(null);
  const [isEditConfirmOpen, setIsEditConfirmOpen] = React.useState(false);
  const isEditingTtn = isTtnDone && editingShiftId !== null && selectedShift?.id === editingShiftId;
  const isTtnLocked = isApprovingTtn || !isShiftEnded || (isTtnDone && !isEditingTtn);

  const handleConfirmEdit = () => {
    setIsEditConfirmOpen(false);
    if (selectedShift) setEditingShiftId(selectedShift.id);
  };
  const handleCancelEdit = () => {
    ttn.discard();
    setEditingShiftId(null);
  };

  const handleApproveTtn = async () => {
    setIsApprovingTtn(true);
    try {
      const isDoneNow = await ttn.save(() => Promise.all([refetchShifts(), refetchAllRoutes()]));
      setEditingShiftId(null);
      // "Approved" only when this write is what closed the TTN: an incomplete one
      // is saved but stays open, and an edit of an approved one is just saved.
      toast({
        title: t("success"),
        description: t(isDoneNow && !isTtnDone ? "ttnApprovedSuccess" : "ttnSavedSuccess"),
      });
    } catch (error) {
      // The draft is kept, so nothing typed is lost and approve can be retried.
      toast({
        title: t("ttnApproveError"),
        description: describeError(error as ApiError),
        variant: "destructive",
      });
    } finally {
      setIsApprovingTtn(false);
    }
  };

  // The document records what was approved: a draft edited since is not on it.
  // The export runs against the shift picked at click time — the form and the
  // selection are locked until it is done, and a shift that changes anyway
  // (a refetch, the URL) cancels it rather than mixing two shifts into one file.
  const [exportingShiftId, setExportingShiftId] = React.useState<bigint | null>(null);
  // Read inside the capture, which runs long after its closure was made.
  const exportingShiftIdRef = React.useRef<bigint | null>(null);
  const setExportingShift = (id: bigint | null) => {
    exportingShiftIdRef.current = id;
    setExportingShiftId(id);
  };
  // The truck's owner is not always loaded with the shift: the company the page
  // is filtered by is the carrier then, as on the route sheet.
  const carrierName = selectedVehicle?.ownerOrganization?.name || selectedCompany?.name;
  const isDownloadingTtn = exportingShiftId !== null;
  const isExportedShiftShown = exportingShiftId !== null && ttn.shift?.id === exportingShiftId;
  const canDownloadTtn = isTtnDone && !ttn.hasChanges;

  React.useEffect(() => {
    if (exportingShiftId !== null && ttn.shift && ttn.shift.id !== exportingShiftId) {
      setExportingShift(null);
    }
  }, [exportingShiftId, ttn.shift]);

  // The click only mounts the off-screen snapshot; the capture, printed or saved, runs
  // once it is laid out (`handleSnapshotReady`).
  const exportActionRef = React.useRef<"download" | "print">("download");
  const handleExportTtn = (action: "download" | "print") => {
    if (!ttn.shift || isDownloadingTtn || !areLocationsReady) return;
    exportActionRef.current = action;
    setExportingShift(ttn.shift.id);
  };

  const handleSnapshotReady = async (nodes: TtnSnapshotNodes) => {
    const shift = ttn.shift;
    try {
      // Mounted for this shift: anything else is a stale snapshot, not the file.
      if (!shift || shift.id !== exportingShiftIdRef.current) return;
      const { generateTtnPdfBlob, buildTtnPdfFileName } = await import("../pdf/ttn-pdf.export");
      const departureAt = fromTimestamp(shift.departureTime);
      const plateNumber = selectedVehicle?.plateNumber ?? "--";
      // The shift's own stamps, not the TTN's выезд / возвращение: those are
      // declared facts and are on the form itself.
      const stamp = (at: Date | null | undefined) => (at ? format(at, "dd.MM.yyyy HH:mm") : "—");
      const blob = await generateTtnPdfBlob({
        title: t("ttnPdf.title", { date: departureAt ? format(departureAt, "dd.MM.yyyy") : "--" }),
        subtitle: [plateNumber, formatDriverName(shift, t("driverNotAssigned")), carrierName]
          .filter(Boolean)
          .join(" · "),
        meta: [
          { label: t("ttnPdf.shiftId"), value: `S${String(shift.id)}` },
          { label: t("ttnPdf.shiftStarted"), value: stamp(fromTimestamp(shift.startedAt)) },
          { label: t("ttnPdf.shiftCompleted"), value: stamp(fromTimestamp(shift.completedAt)) },
          {
            label: t("ttnPdf.approvedAt"),
            value: stamp(fromTimestamp(shift.consignmentNoteDoneAt)),
          },
        ],
        sections: [
          { title: t("mainInfo"), node: nodes.info },
          ...(nodes.jobs ? [{ title: t("completedJobs"), node: nodes.jobs }] : []),
        ],
      });
      // The capture takes a while; if the shift changed under it, the file
      // would carry one shift's name over another's pages.
      if (exportingShiftIdRef.current !== shift.id) return;
      if (exportActionRef.current === "print") printBlob(blob);
      else downloadBlob(blob, buildTtnPdfFileName(plateNumber, shift));
    } catch (error) {
      toast({
        title: t("ttnDownloadError"),
        description: String((error as Error)?.message ?? error),
        variant: "destructive",
      });
    } finally {
      // Not a run that was cancelled and has already been replaced.
      if (exportingShiftIdRef.current === shift?.id) setExportingShift(null);
    }
  };

  const infoContentProps = {
    shift: ttn.shift,
    routes: ttn.routes,
    tripTimes: ttn.tripTimes,
    sources: ttn.sources,
    agreement: ttn.agreement,
    depots,
    crewCandidates,
    onShiftChange: ttn.setShift,
    onCrewChange: ttn.setCrew,
    onRouteChange: ttn.setRoute,
    options: ttn.options,
    edits: ttn.edits,
    returnMileage: ttn.returnMileage,
  };
  const cascadeProps =
    orgId === null
      ? null
      : {
          routes: ttn.routes,
          selectedDate,
          onRefetch: refetchAllRoutes,
          showSelection: false,
          ttn: {
            orgId,
            onDestinationChange: (route: ConnectProto.Route, destination: ConnectProto.Location) =>
              ttn.setRoute(route, { destination }),
            disabled: isTtnLocked || isDownloadingTtn,
            tripTimes: ttn.tripTimes,
          },
        };

  // The PDF carries the TTN as stored: nothing the form only fills in on the fly.
  const storedView = isExportedShiftShown ? storedTtnView(infoContentProps) : null;

  // No trips, no track to play back: the map is left out and the content takes
  // its width. Kept while the trips load, so the layout doesn't jump.
  const showMap = focusedShiftLoading || ttn.routes.length > 0;

  const jobsCount = React.useMemo(
    () => shownShiftRoutes.reduce((acc, route) => acc + (route.jobs?.length ?? 0), 0),
    [shownShiftRoutes],
  );

  const approve =
    !isTtnDone || isEditingTtn
      ? {
          onClick: handleApproveTtn,
          isLoading: isApprovingTtn,
          hasUnsavedChanges: ttn.hasChanges,
          blockedReason: !isShiftEnded
            ? t("ttnShiftNotEnded")
            : ttn.routes.length === 0
              ? t("ttnApproveNoTrips")
              : ttn.unfinishedJobsCount > 0
                ? t("ttnJobsNotFinished", { count: ttn.unfinishedJobsCount })
                : ttn.isShiftTimeInverted
                  ? t("ttnShiftDepartureAfterReturn")
                  : ttn.invertedTripsCount > 0
                    ? t("ttnTripStartAfterUnload", { count: ttn.invertedTripsCount })
                    : undefined,
        }
      : undefined;
  const edit = isTtnDone
    ? {
        isEditing: isEditingTtn,
        onStart: () => setIsEditConfirmOpen(true),
        onCancel: handleCancelEdit,
        disabled: isApprovingTtn || isDownloadingTtn,
      }
    : undefined;

  const shiftTabs = selectedVehicleShifts.map((shift, index) => ({
    id: shift.id,
    status: shift.status,
    label: t("shiftIndex", { index: index + 1 }),
  }));

  return (
    <div className="w-full h-full bg-white rounded-xl border border-ds-border-primary flex-1 overflow-hidden">
      <div className="h-full w-full grid grid-cols-[196px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
        {/* ── Left: vehicle list ─────────────────────────────────────── */}
        <MswRouteListSidebar
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          searchValue={vehicleSearch}
          onSearchChange={setVehicleSearch}
          mode={sidebarMode}
          onModeChange={setSidebarMode}
          sections={sidebarSections}
          selectedId={selectedId}
          onSelect={(id, shiftId) => {
            if (isDownloadingTtn) return;
            handleSidebarSelect(id, shiftId);
          }}
          routesByShiftId={allRoutesByShiftId}
          isShiftFlagged={isTtnNotApproved}
          variant="ttn"
        />

        <div className={cn("flex min-h-0 min-w-0 flex-col", TTN_MAP_WIDTH_VARS)}>
          {/* ── Shift tabs, approval state and the document's actions ─── */}
          <ShiftHeader
            tabs={shiftTabs}
            activeId={activeShiftId}
            onChange={(id) => {
              if (isDownloadingTtn) return;
              handleToggleShift(id);
            }}
            actions={
              selectedShift && (
                <>
                  {!isTtnDone && <ApprovalPill>{t("ttnNotApproved")}</ApprovalPill>}
                  <TtnHeaderActions
                    approve={approve}
                    edit={edit}
                    document={{
                      onPrint: () => handleExportTtn("print"),
                      onDownload: () => handleExportTtn("download"),
                      isLoading: isDownloadingTtn || !areLocationsReady,
                      disabledReason: canDownloadTtn
                        ? undefined
                        : isTtnDone
                          ? t("ttnDownloadUnsavedChanges")
                          : t("ttnDownloadNotApproved"),
                    }}
                  />
                </>
              )
            }
          />

          <div className="flex min-h-0 flex-1">
            {/* ── Centre: vehicle summary, sub-tabs, TTN content ──────────── */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="p-6">
                <ShiftSummary
                  vehicleFirst
                  shift={selectedShift}
                  vehicle={selectedVehicle}
                  orgName={carrierName}
                  date={selectedDate}
                  ttnApproved={isTtnDone}
                  driverFallback={t("driverNotAssigned")}
                />
              </div>

              <FolderTabs
                className="h-9"
                value={activeTab}
                onChange={setActiveTab}
                tabs={[
                  { value: "info", label: t("mainInfo") },
                  {
                    value: "jobs",
                    label: (
                      <>
                        {t("completedJobs")}
                        {jobsCount > 0 && <CountBadge variant="ds" count={jobsCount} />}
                      </>
                    ),
                  },
                ]}
              />

              {selectedVehicleShifts.length === 0 ? (
                <EmptyState className="h-32">{t("noRoutes")}</EmptyState>
              ) : (
                <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-auto p-6">
                  {activeTab === "info" &&
                    (focusedShiftLoading ? (
                      <EmptyState>{t("loadingRoutes")}</EmptyState>
                    ) : (
                      <>
                        <SourceLegend />
                        <TtnRouteInfoContent
                          {...infoContentProps}
                          disabled={isTtnLocked || isDownloadingTtn}
                          approve={approve}
                          edit={edit}
                        />
                      </>
                    ))}

                  {activeTab === "jobs" &&
                    activeShiftId !== null &&
                    (focusedShiftLoading ? (
                      <EmptyState>{t("loadingRoutes")}</EmptyState>
                    ) : ttn.routes.length === 0 || cascadeProps === null ? (
                      <EmptyState>{t("noRoutes")}</EmptyState>
                    ) : (
                      <MswRouteCascade
                        {...cascadeProps}
                        expandedRouteId={expandedRouteId ?? null}
                        onExpand={(routeId) => handleExpandRoute(activeShiftId, routeId)}
                        onSelectionMapChange={handleShiftSelectionMapChange}
                      />
                    ))}

                  {activeTab === "fuel" && (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                      {t("fuelAccountingInDevelopment")}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Track playback of the shift — same map, player and table as "Контроль движения". */}
            {showMap && (
              <CollapsibleMapPanel
                collapsed={mapCollapsed}
                onToggle={toggleMapCollapsed}
                expandedWidthClassName={TTN_MAP_PANEL_WIDTH}
                className="!rounded-none"
                frameClassName="border-l border-ds-border-primary"
              >
                {/* Collapsed, the strip shows a bare edge of the map, as on the routes page. */}
                <ShiftPlayback
                  shift={selectedShift}
                  truck={selectedShift?.truck ?? selectedVehicle}
                  routes={ttn.playbackRoutes}
                  depots={depots}
                  window={ttn.trackWindow}
                  reserveTopRightSlot
                  factOnly={isTtnDone}
                  showPlaybackInfo={false}
                  collapsed={mapCollapsed}
                  flush
                />
              </CollapsibleMapPanel>
            )}
          </div>
        </div>
      </div>

      <TtnEditConfirmModal
        open={isEditConfirmOpen}
        onOpenChange={setIsEditConfirmOpen}
        onConfirm={handleConfirmEdit}
      />

      {/* Both tabs as on the platform, read-only and at full height, for the PDF. */}
      {storedView && (
        <TtnSnapshotHost
          // The legend sits on the page, not in the form: the document needs its own copy.
          info={
            <div className="flex flex-col gap-4">
              <SourceLegend />
              <TtnRouteInfoContent {...storedView} disabled />
            </div>
          }
          jobs={
            cascadeProps &&
            ttn.routes.length > 0 && (
              <MswRouteCascade
                {...cascadeProps}
                ttn={{
                  ...cascadeProps.ttn,
                  disabled: true,
                  tripTimes: storedView.tripTimes,
                }}
                expandAll
                expandedRouteId={null}
                onExpand={() => undefined}
              />
            )
          }
          onReady={handleSnapshotReady}
        />
      )}
    </div>
  );
};

export default TtnRouteListPage;
